import type {
  SeriesCriteria,
  ClassificationSummary,
  SeriesClassificationSummary,
} from 'generated/graphql';
import type {
  FullSingleLayerStack,
  Study,
  Series,
  CompositeMatchType,
  SeriesMatch,
  CompositeStackConfig,
  HangTheseObject,
} from '../../../ViewportsConfigurations/types';

/**
 * All series criteria category keys that we use for matching.
 */
export enum SeriesCriteriaKey {
  ANATOMIC_FOCUS = 'anatomic_focus',
  CONTRAST_STATUS = 'contrast_status',
  CT_KERNEL = 'ct_kernel',
  DIMENSIONALITY = 'dimensionality',
  DOCUMENT_TYPE = 'document_type',
  FIELD_OF_VIEW = 'field_of_view',
  LOCALIZER = 'localizer',
  MODALITY = 'modality',
  MR_RELATIVE_PHASE = 'mr_relative_phase',
  MR_WEIGHTING = 'mr_weighting',
  PATIENT_POSITION = 'patient_position',
  POST_PROCESSING = 'post_processing',
  TISSUE_SELECTION = 'tissue_selection',
  VIEW_ORIENTATION = 'view_orientation',
}

/** Array of all SeriesCriteriaKey values for iteration. */
export const SERIES_CRITERIA_KEYS = Object.values(SeriesCriteriaKey);

/**
 * Normalized series match criteria extracted from SeriesCriteria.
 * Each key maps to an array of ontology_ids (preferred) or display_names.
 * Null means the category was not present in the classification.
 */
type SeriesMatchCriteria = {
  readonly [K in SeriesCriteriaKey]: ReadonlyArray<string> | null;
};

/**
 * Criteria keys that MUST match for hybrid stacks (within same exam).
 */
const HYBRID_REQUIRED_CRITERIA: ReadonlyArray<SeriesCriteriaKey> = [
  SeriesCriteriaKey.ANATOMIC_FOCUS,
  SeriesCriteriaKey.VIEW_ORIENTATION,
  SeriesCriteriaKey.FIELD_OF_VIEW,
];

/**
 * Criteria keys that must DIFFER for hybrid stacks (within same exam).
 * This ensures we only combine series with different dimensionality (e.g., 2D + Tomo).
 */
const HYBRID_MUST_DIFFER_CRITERIA: ReadonlyArray<SeriesCriteriaKey> = [
  SeriesCriteriaKey.DIMENSIONALITY,
];

/**
 * Extracts a normalized identifier from a ClassificationSummary.
 * Prefers ontology_id (more stable), falls back to display_name.
 */
function extractClassificationId(classification: Nullable<ClassificationSummary>): string | null {
  if (classification == null) return null;
  return classification.ontology_id ?? classification.display_name ?? null;
}

/**
 * Extracts normalized match criteria from a SeriesCriteria object.
 * Each category becomes an array of identifiers (sorted for comparison).
 * Returns null for categories that have no classifications.
 */
export function extractMatchCriteria(
  seriesCriteria: Nullable<SeriesCriteria>
): SeriesMatchCriteria {
  const result: Record<string, ReadonlyArray<string> | null> = {};

  for (const key of SERIES_CRITERIA_KEYS) {
    const classifications = seriesCriteria?.[key];

    if (classifications == null || classifications.length === 0) {
      result[key] = null;
    } else {
      const ids = classifications
        .map(extractClassificationId)
        .filter((id): id is string => id != null)
        .sort();

      result[key] = ids.length > 0 ? ids : null;
    }
  }

  return result as SeriesMatchCriteria;
}

/**
 * Compares two arrays of strings for exact equality (order-independent).
 * Both arrays should already be sorted.
 */
function arraysEqual(a: ReadonlyArray<string> | null, b: ReadonlyArray<string> | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
}

/**
 * Checks if two series match on all populated criteria categories.
 * "Available" means both series have non-null values for that category.
 * If one series has a category and the other doesn't, they don't match.
 *
 * @param primary - The top series criteria
 * @param candidate - The candidate series criteria to compare
 * @returns true if all populated categories match exactly
 */
export function seriesMatchesCriteria(
  primary: SeriesMatchCriteria,
  candidate: SeriesMatchCriteria
): boolean {
  for (const key of SERIES_CRITERIA_KEYS) {
    const primaryVal = primary[key];
    const candidateVal = candidate[key];

    if (primaryVal !== null || candidateVal !== null) {
      if (!arraysEqual(primaryVal, candidateVal)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Checks if two series match using hybrid criteria:
 * - Required criteria (anatomic_focus, view_orientation, field_of_view) must match
 * - Must-differ criteria (dimensionality) must NOT match
 */
export function seriesMatchesHybridCriteria(
  primary: SeriesMatchCriteria,
  candidate: SeriesMatchCriteria
): boolean {
  for (const key of HYBRID_REQUIRED_CRITERIA) {
    const primaryVal = primary[key];
    const candidateVal = candidate[key];

    if (primaryVal !== null || candidateVal !== null) {
      if (!arraysEqual(primaryVal, candidateVal)) {
        return false;
      }
    }
  }

  for (const key of HYBRID_MUST_DIFFER_CRITERIA) {
    const primaryVal = primary[key];
    const candidateVal = candidate[key];

    if (primaryVal === null || candidateVal === null) {
      return false;
    }

    if (arraysEqual(primaryVal, candidateVal)) {
      return false;
    }
  }

  return true;
}

/**
 * Gets the series classification and description for a stack's first frame's series.
 */
function getSeriesInfoForStack(
  stack: FullSingleLayerStack,
  study: Study
): {
  classification: SeriesClassificationSummary | null;
  seriesDescription: string | null;
  seriesSmid: string | null;
} {
  if (!('frames' in stack) || stack.frames.length === 0) {
    return { classification: null, seriesDescription: null, seriesSmid: null };
  }

  const firstFrameSeriesSmid = stack.frames[0].series.smid;
  const series = study.seriesList.find((s) => s.smid === firstFrameSeriesSmid) as
    | Series
    | undefined;

  if (series == null) {
    return { classification: null, seriesDescription: null, seriesSmid: null };
  }

  const seriesDescription = 'description' in series ? (series.description as string | null) : null;

  return {
    classification: series.classifications?.[0] ?? null,
    seriesDescription,
    seriesSmid: series.smid,
  };
}

/**
 * Gets the series classification for a series SMID directly.
 */
function getSeriesInfoBySmid(
  seriesSmid: string,
  study: Study
): { classification: SeriesClassificationSummary | null; seriesDescription: string | null } {
  const series = study.seriesList.find((s) => s.smid === seriesSmid) as Series | undefined;

  if (series == null) {
    return { classification: null, seriesDescription: null };
  }

  const seriesDescription = 'description' in series ? (series.description as string | null) : null;

  return {
    classification: series.classifications?.[0] ?? null,
    seriesDescription,
  };
}

/**
 * Finds a single-layer stack for a given series. Prefers the stack matching
 * activeStackSmid (the stack currently hung in the viewport), otherwise
 * returns the first stack whose frames belong to the series.
 *
 * A series can have multiple stacks (e.g. different reconstructions or frame
 * groupings), so activeStackSmid is used to select the right one when available.
 */
function findStackForSeries(
  seriesSmid: string,
  study: Study,
  activeStackSmid?: string | null
): FullSingleLayerStack | null {
  let firstMatch: FullSingleLayerStack | null = null;

  for (const stack of study.stackedFrames) {
    if (stack.__typename !== 'SingleLayerStack') continue;

    const fullStack = stack as FullSingleLayerStack;
    if (!('frames' in fullStack) || fullStack.frames.length === 0) continue;

    if (fullStack.frames[0].series.smid !== seriesSmid) continue;

    if (fullStack.smid === activeStackSmid) return fullStack;
    if (firstMatch == null) firstMatch = fullStack;
  }

  return firstMatch;
}

/**
 * Finds matching series WITHIN THE SAME EXAM (for hybrid stacks like 2D + Tomo).
 * Uses hybrid matching (ignores dimensionality) to find related series.
 *
 * @param topSeriesSmid - The series SMID of the top series
 * @param study - The study to search within
 * @returns Array of matching series SMIDs (excluding the top), or empty array
 */
export function findMatchingSeriesWithinStudy(topSeriesSmid: string, study: Study): string[] {
  const { classification: topClassification } = getSeriesInfoBySmid(topSeriesSmid, study);

  if (topClassification?.seriesCriteria == null) {
    return [];
  }

  const topCriteria = extractMatchCriteria(topClassification.seriesCriteria);
  const matches: string[] = [];

  for (const series of study.seriesList) {
    if (series.smid === topSeriesSmid) continue;

    const { classification: candidateClassification } = getSeriesInfoBySmid(series.smid, study);

    if (candidateClassification?.seriesCriteria == null) {
      continue;
    }

    const candidateCriteria = extractMatchCriteria(candidateClassification.seriesCriteria);

    if (seriesMatchesHybridCriteria(topCriteria, candidateCriteria)) {
      matches.push(series.smid);
    }
  }

  return matches;
}

/**
 * Finds matching series and returns them in the hang_these API format.
 * This is the shape we'll receive from the backend once matching moves server-side.
 *
 * @param topSeriesSmid - The series SMID of the top (hung) series
 * @param topStudy - The study containing the top series
 * @param allStudies - All loaded studies (current + priors)
 * @param mainStudyIds - IDs of the main (non-prior) studies
 * @param matchType - Which matching mode to use ('hybrid' or 'prior')
 * @returns Array of HangTheseItem in comparison_index order, or null if no top series found
 */
function findMatchingSeries(
  topSeriesSmid: string,
  topStudy: Study,
  allStudies: ReadonlyArray<Study>,
  mainStudyIds: ReadonlyArray<string>,
  matchType: CompositeMatchType
): HangTheseObject[] | null {
  const { classification: topClassification } = getSeriesInfoBySmid(topSeriesSmid, topStudy);
  if (topClassification?.seriesCriteria == null) {
    return null;
  }

  const topCriteria = extractMatchCriteria(topClassification.seriesCriteria);

  // Primary item has null comparisonIndex (current exam, not a comparison)
  const hangThese: HangTheseObject[] = [
    {
      studySmid: topStudy.smid,
      objectType: 'series',
      objectSmid: topSeriesSmid,
      comparisonIndex: null,
      matchType,
    },
  ];

  if (matchType === 'hybrid') {
    // Hybrid matches are within the same exam — keep null comparisonIndex
    const hybridMatches = findMatchingSeriesWithinStudy(topSeriesSmid, topStudy);
    for (const seriesSmid of hybridMatches) {
      hangThese.push({
        studySmid: topStudy.smid,
        objectType: 'series',
        objectSmid: seriesSmid,
        comparisonIndex: null,
        matchType: 'hybrid',
      });
    }
  }

  if (matchType === 'prior') {
    type MatchWithDate = {
      item: Omit<HangTheseObject, 'comparisonIndex'>;
      studyDate: string | null;
    };
    const matchesWithDates: MatchWithDate[] = [];
    const matchedStudySmids = new Set<string>();

    for (const study of allStudies) {
      if (study.smid === topStudy.smid || mainStudyIds.includes(study.smid)) {
        continue;
      }

      const studyDate = 'studyDate' in study ? (study.studyDate as string | null) : null;

      for (const series of study.seriesList) {
        // Limit to one match per prior study
        if (matchedStudySmids.has(study.smid)) {
          break;
        }

        const { classification: candidateClassification } = getSeriesInfoBySmid(series.smid, study);

        if (candidateClassification?.seriesCriteria == null) {
          continue;
        }

        const candidateCriteria = extractMatchCriteria(candidateClassification.seriesCriteria);

        if (seriesMatchesCriteria(topCriteria, candidateCriteria)) {
          matchedStudySmids.add(study.smid);
          matchesWithDates.push({
            item: {
              studySmid: study.smid,
              objectType: 'series',
              objectSmid: series.smid,
              matchType: 'prior' as const,
            },
            studyDate,
          });
        }
      }
    }

    // Sort by date descending (most recent prior first)
    matchesWithDates.sort((a, b) => {
      if (a.studyDate == null && b.studyDate == null) return 0;
      if (a.studyDate == null) return 1;
      if (b.studyDate == null) return -1;
      return b.studyDate.localeCompare(a.studyDate);
    });

    // Assign 0-based comparison indices to priors
    for (let i = 0; i < matchesWithDates.length; i++) {
      hangThese.push({
        ...matchesWithDates[i].item,
        comparisonIndex: i,
      });
    }
  }

  return hangThese;
}

/**
 * Resolves a hang_these array to a CompositeStackConfig.
 * This converts the backend API format to the format used by MultiObjectImagingProvider.
 *
 * @param hangThese - Array of HangTheseObject from backend or local matching
 * @param allStudies - All loaded studies to resolve SMIDs
 * @param activeStackSmid - SMID of the stack currently hung in the viewport (preferred for selection)
 * @returns CompositeStackConfig or null if resolution fails
 */
export function resolveHangTheseToConfig(
  hangThese: ReadonlyArray<HangTheseObject>,
  allStudies: ReadonlyArray<Study>,
  activeStackSmid?: string | null
): CompositeStackConfig | null {
  if (hangThese.length === 0) {
    return null;
  }

  // Sort: null comparisonIndex (current exam) first, then ascending by index
  const sorted = [...hangThese].sort((a, b) => {
    if (a.comparisonIndex == null && b.comparisonIndex == null) return 0;
    if (a.comparisonIndex == null) return -1;
    if (b.comparisonIndex == null) return 1;
    return a.comparisonIndex - b.comparisonIndex;
  });

  // The primary item must have null comparisonIndex (current exam)
  const topItem = sorted[0];
  if (topItem.comparisonIndex != null) {
    return null;
  }

  const topStudy = allStudies.find((s) => s.smid === topItem.studySmid);
  if (topStudy == null) {
    return null;
  }

  const topStack = findStackForSeries(topItem.objectSmid, topStudy, activeStackSmid);
  if (topStack == null) {
    return null;
  }

  const { seriesDescription: topSeriesDescription } = getSeriesInfoForStack(topStack, topStudy);

  const matches: SeriesMatch[] = [];

  for (const item of sorted.slice(1)) {
    const matchedStudy = allStudies.find((s) => s.smid === item.studySmid);
    if (matchedStudy == null) {
      continue;
    }

    const matchedStack = findStackForSeries(item.objectSmid, matchedStudy);
    if (matchedStack == null) {
      continue;
    }

    const { seriesDescription: matchedSeriesDescription } = getSeriesInfoForStack(
      matchedStack,
      matchedStudy
    );

    matches.push({
      stack: matchedStack,
      study: matchedStudy,
      studyDate: 'studyDate' in matchedStudy ? (matchedStudy.studyDate as string | null) : null,
      seriesDescription: matchedSeriesDescription,
      matchType: item.matchType,
    });
  }

  if (matches.length === 0) {
    return null;
  }

  return {
    topStack,
    topStudy,
    topSeriesDescription,
    matches,
  };
}

/**
 * Builds a CompositeStackConfig for hybrid stacking (within same exam, e.g. 2D + Tomo).
 * Returns null if there are no hybrid matches.
 */
export function buildHybridStackConfig(
  topStack: FullSingleLayerStack,
  topStudy: Study
): CompositeStackConfig | null {
  const { seriesSmid: topSeriesSmid } = getSeriesInfoForStack(topStack, topStudy);
  if (topSeriesSmid == null) {
    return null;
  }

  const hangThese = findMatchingSeries(topSeriesSmid, topStudy, [topStudy], [], 'hybrid');

  if (hangThese == null || hangThese.length <= 1) {
    return null;
  }

  return resolveHangTheseToConfig(hangThese, [topStudy], topStack.smid);
}

/**
 * Builds a CompositeStackConfig for prior series matching (across different exams).
 * Returns null if there are no prior matches.
 */
export function buildPriorSeriesConfig(
  topStack: FullSingleLayerStack,
  topStudy: Study,
  allStudies: ReadonlyArray<Study>,
  mainStudyIds: ReadonlyArray<string>
): CompositeStackConfig | null {
  const { seriesSmid: topSeriesSmid } = getSeriesInfoForStack(topStack, topStudy);
  if (topSeriesSmid == null) {
    return null;
  }

  const hangThese = findMatchingSeries(topSeriesSmid, topStudy, allStudies, mainStudyIds, 'prior');

  if (hangThese == null || hangThese.length <= 1) {
    return null;
  }

  return resolveHangTheseToConfig(hangThese, allStudies, topStack.smid);
}
